import React, {useEffect, useRef, useState} from 'react'
import Select, { SingleValue } from 'react-select'
import { getReassigner } from '../../../components/requests'

type User = {
  id: number
  prenom: string
  nom: string
  email: string
  utilisateurEquipes: any
}

type OptionType = {
  value: number
  label: string
}

type ReassignDemandeModalProps = {
  demandeId: number
  utilisateurs: User[]
  onReassign: (demandeId: number, userId: number) => void
  utilisateurAssign?: Number | undefined
  equipe?: string | null | undefined
}


const ReassignDemandeModal = ({ demandeId, onReassign, utilisateurAssign = 0, equipe = null }: ReassignDemandeModalProps) => {
  const [selectedUser, setSelectedUser] = useState<OptionType | null>(null)
  const [loading, setLoading] = useState(false)
  const [users, setUsers] = useState<User[]>([])
  const modalRef = useRef<HTMLDivElement | null>(null)
  const [alreadyFetched, setAlreadyFetched] = useState<boolean>(false)

  const loadTelevendeurs = async () => {
    setLoading(true)
    try {
      const data = await getReassigner()
      setUsers(data?.map((item) => ({
        ...item,
        utilisateurEquipes: item?.utilisateurEquipes?.map((e) => ({
          id: e?.equipe?.id,
          nom: e?.equipe?.nom,
        }))
      })))
    } catch (error) {
      console.error('Erreur chargement télévendeurs :', error)
    } finally {
      setLoading(false)
    }
  }

  // Gestion ouverture et fermeture du modal, cleanUp
  useEffect(() => {
    const modal = modalRef.current

    if (modal) {
      modal.addEventListener('shown.bs.modal', () => {
        // Récupération unique à la première ouverture du modal
        setAlreadyFetched(true)
      })
    }

    // Nettoyer l'event listener quand le composant se démonte
    return () => {
      if (modal) {
        modal.removeEventListener('shown.bs.modal', () => {})
      }
    }
  }, [])

  useEffect(() => {
    if (alreadyFetched) {
      loadTelevendeurs()
    }
  }, [alreadyFetched])

  const handleChange = (option: SingleValue<OptionType>) => {
    setSelectedUser(option)
  }

  const handleConfirm = async () => {
    if (!selectedUser) return
    setLoading(true)
    try {
      await onReassign(demandeId, selectedUser.value)
      setSelectedUser(null)
    } catch (error) {
      console.error('Erreur de réassignation :', error)
    } finally {
      setLoading(false)
    }
  }

  const options: OptionType[] = users
    .filter((u) =>
      ((u.prenom?.trim() && u.nom?.trim()) || u.email?.trim())
      && u.id !== utilisateurAssign
      && u?.utilisateurEquipes?.some((e) => e?.nom === equipe)
    )
    .map((u) => ({
      value: u.id,
      label: (u.prenom && u.nom) ? `${u.prenom} ${u.nom?.toUpperCase()}` : `${u.email}`,
      prenom: u?.prenom,
      nom: u?.nom,
    }))

  return (
    <div
      className="modal fade"
      id="modalReassign"
      tabIndex={-1}
      aria-labelledby="modalReassignLabel"
      aria-hidden="true"
      ref={modalRef}
    >
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="modalReassignLabel">
              Réassigner la demande
            </h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Fermer"
            ></button>
          </div>
          <div className="modal-body">
            <Select
              options={options}
              value={selectedUser}
              onChange={handleChange}
              placeholder="Sélectionner un utilisateur"
              className="react-select"
              classNamePrefix="react-select"
              isClearable
              isLoading={loading}
              formatOptionLabel={(option, { context }) => {
                if (context === "value") return <span>{option.prenom} <strong>{option.nom?.toUpperCase()}</strong></span>
                return (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 12,
                    }}
                  >
                    <span>{option.prenom}</span>
                    <strong>{option.nom?.toUpperCase()}</strong>
                  </div>
                );
              }}
            />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
              Annuler
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleConfirm}
              disabled={!selectedUser || loading}
              data-bs-dismiss={!loading ? 'modal' : undefined}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>Chargement...
                </>
              ) : (
                'Confirmer'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export { ReassignDemandeModal }